import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// Removed HighchartsMore/SolidGauge to fix webpack error
import { FiSearch, FiFilter, FiEdit2, FiCheckCircle, FiXCircle, FiClock, FiChevronLeft, FiChevronRight, FiAlertCircle, FiRefreshCw, FiCalendar, FiFileText, FiChevronUp, FiChevronDown, FiTrendingUp, FiCoffee, FiArrowLeft, FiArrowRight, FiDownload, FiInfo, FiTrash2 } from 'react-icons/fi'; // Icons
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useUser } from '../../contexts/UserContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RiFilePdf2Fill, RiFileExcelFill, RiFileHistoryFill, RiDownload2Fill } from "react-icons/ri";
import { toJpeg } from 'html-to-image';

// Init Chart Modules
// HighchartsMore(Highcharts);
// SolidGauge(Highcharts);

// --- Register View Component (Moved outside to prevent re-mounts) ---
const RegisterView = ({ registerDate, setRegisterDate, handleManualClick, refreshTrigger, handlePunchEdit, isHROrAdmin }) => {
    const [localData, setLocalData] = useState([]);
    const [rLoading, setRLoading] = useState(false);

    // Bulk Actions State
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('PRESENT');
    const [bulkReason, setBulkReason] = useState('Bulk Action');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);

    const fetchRegisterData = async () => {
        setRLoading(true);
        setSelectedIds([]); // Clear selection on date change
        try {
            const res = await api.get('/attendance/data', {
                params: { startDate: registerDate, endDate: registerDate }
            });
            if (res.data.status) {
                setLocalData(res.data.data);
            }
        } catch (e) { toast.error('Failed to load register'); }
        finally { setRLoading(false); }
    };

    useEffect(() => { fetchRegisterData(); }, [registerDate, refreshTrigger]);

    // Selection Logic
    const handleSelectRow = (userId) => {
        setSelectedIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(localData.map(d => d.user_id));
        } else {
            setSelectedIds([]);
        }
    };

    const executeBulkAction = async () => {
        if (selectedIds.length === 0) return;
        setShowBulkConfirm(true);
    };

    const confirmBulkAction = async () => {
        setShowBulkConfirm(false);
        setBulkLoading(true);
        try {
            await api.post('/attendance/bulk-update-status', { // Use new endpoint
                user_ids: selectedIds,
                date: registerDate,
                status: bulkStatus,
                reason: bulkReason
            });
            toast.success('Bulk update successful');
            setSelectedIds([]);
            fetchRegisterData();
        } catch (e) {
            toast.error('Bulk update failed');
        } finally {
            setBulkLoading(false);
        }
    };

    // Counts
    const countP = localData.filter(d => d.status === 'PRESENT' || d.status === 'LATE').length;
    const countA = localData.filter(d => d.status === 'ABSENT').length;
    const countOD = localData.filter(d => d.status === 'ON_DUTY').length;
    const countL = localData.filter(d => d.status === 'ON_LEAVE').length;
    const countPER = localData.filter(d => d.status === 'PERMISSION').length;

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 animate-fade-in-up">
            {/* Header Controls */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <h3 className="font-bold text-lg text-gray-800">Date:</h3>
                    <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200">
                        <button onClick={() => {
                            const d = new Date(registerDate); d.setDate(d.getDate() - 1);
                            setRegisterDate(d.toISOString().split('T')[0]);
                        }} className="p-1 hover:bg-gray-200 rounded"><FiChevronLeft /></button>
                        <input
                            type="date"
                            value={registerDate}
                            onChange={e => setRegisterDate(e.target.value)}
                            className="bg-transparent border-none text-sm font-bold text-gray-700 focus:ring-0"
                        />
                        <button onClick={() => {
                            const d = new Date(registerDate); d.setDate(d.getDate() + 1);
                            setRegisterDate(d.toISOString().split('T')[0]);
                        }} className="p-1 hover:bg-gray-200 rounded"><FiChevronRight /></button>

                    </div>
                </div>

                <div className="flex gap-4 font-bold text-sm bg-gray-50 p-2 rounded-lg">
                    <div className="text-green-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> P: {countP}</div>
                    <div className="text-red-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> A: {countA}</div>
                    <div className="text-blue-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> OD: {countOD}</div>
                    <div className="text-orange-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> L: {countL}</div>
                    <div className="text-purple-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span> PER: {countPER}</div>
                </div>
            </div>

            {/* Bulk Actions Banner */}
            {selectedIds.length > 0 && (
                <div className="mb-4 bg-blue-50 border border-blue-100 p-4 rounded-xl flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in-down">
                    <div className="flex items-center gap-2">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">{selectedIds.length} Selected</span>
                        <span className="text-sm text-blue-800 font-medium">Apply status to selected employees:</span>
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <select
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                            className="text-sm border-blue-200 rounded-lg focus:ring-blue-500"
                        >
                            <option value="PRESENT">Present</option>
                            <option value="ABSENT">Absent</option>
                            <option value="ON_LEAVE">On Leave</option>
                            <option value="ON_DUTY">On Duty</option>
                            <option value="HOLIDAY">Holiday</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Reason (Optional)"
                            value={bulkReason}
                            onChange={(e) => setBulkReason(e.target.value)}
                            className="text-sm border-blue-200 rounded-lg focus:ring-blue-500"
                        />
                        <button
                            onClick={executeBulkAction}
                            disabled={bulkLoading}
                            className="bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {bulkLoading ? 'Applying...' : 'Apply'}
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                    <thead className="text-xs uppercase bg-gray-50 text-gray-600 font-bold border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-3 w-10 text-center">
                                <input type="checkbox" onChange={handleSelectAll} checked={localData.length > 0 && selectedIds.length === localData.length} />
                            </th>
                            <th className="px-4 py-3 w-16 text-center">No.</th>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Shift</th>
                            <th className="px-4 py-3">Sched</th>
                            <th className="px-4 py-3 text-center">Days</th>
                            <th className="px-4 py-3 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rLoading ? (
                            <tr><td colSpan="7" className="text-center py-8">Loading...</td></tr>
                        ) : localData.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-8">No records found.</td></tr>
                        ) : localData.map((row, i) => (
                            <tr key={row.user_id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(row.user_id) ? 'bg-blue-50' : ''}`}>
                                <td className="px-4 py-3 text-center">
                                    <input type="checkbox" checked={selectedIds.includes(row.user_id)} onChange={() => handleSelectRow(row.user_id)} />
                                </td>
                                <td className="px-4 py-3 text-center text-gray-500">{i + 1}</td>
                                <td className="px-4 py-3 font-bold text-gray-800">{row.name}</td>
                                <td className="px-4 py-3 text-gray-600">{row.shift || '-'}</td>
                                <td
                                    className={`px-4 py-3 text-xs text-gray-500 max-w-[280px] break-words ${isHROrAdmin ? 'cursor-pointer hover:bg-yellow-50' : ''}`}
                                    onDoubleClick={() => isHROrAdmin && handlePunchEdit(row)}
                                    title={isHROrAdmin ? "Double click to edit punch" : ""}
                                >
                                    {row.sched_reason ? (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-purple-700 font-bold italic">{row.sched_reason}</span>
                                            {row.punch_records && row.punch_records !== '-' && (
                                                <div className="mt-1 flex flex-col border-t border-gray-100 pt-1">
                                                    <span className="text-[9px] text-gray-400 font-bold uppercase text-[8px]">Biometric Records:</span>
                                                    <span className="text-[10px] text-gray-500">{row.punch_records}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : ((row.status === 'HALF_DAY' || row.status === 'PERMISSION') && (row.permission_start_time || row.permission_end_time)) ? (
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 w-fit">
                                                <span className="text-purple-700 font-bold uppercase text-[9px]">Timing:</span>
                                                <span className="text-gray-700 font-medium text-[10px]">{row.permission_start_time} - {row.permission_end_time}</span>
                                            </div>
                                            {row.punch_records && row.punch_records !== '-' && (
                                                <div className="flex flex-col mt-1">
                                                    <span className="text-[9px] text-gray-400 font-medium uppercase">Logs: {row.punch_records}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : row.is_manual_override == 1 ? (
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1">
                                                <span className="text-blue-600 font-bold italic text-[9px] uppercase bg-blue-50 px-1 rounded border border-blue-100">Manual Override</span>
                                                {row.punch_records && row.punch_records !== '-' && <span className="text-[9px] text-gray-400 font-medium">(Real punches exist)</span>}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-medium text-gray-700">In: {row.checkIn}</span>
                                                <span className="font-medium text-gray-700">Out: {row.checkOut}</span>
                                            </div>
                                            {row.punch_records && row.punch_records !== '-' && (
                                                <div className="mt-1 flex flex-col border-t border-gray-100 pt-1">
                                                    <span className="text-[9px] text-gray-400 font-bold uppercase">Biometric Logs:</span>
                                                    <span className="text-[10px] text-gray-500">{row.punch_records}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-gray-600 font-medium italic">{row.punch_records || '-'}</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center font-bold text-gray-700">{row.present_days}</td>
                                <td className="px-4 py-3 text-center">
                                    <div className="flex justify-center gap-1">
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'PRESENT')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all relative ${row.status === 'PRESENT' || row.status === 'LATE' ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-green-50 hover:text-green-600'}`}
                                            title={(row.punch_records && row.punch_records !== '-') ? "Punches detected - Mark Present" : "Mark Present"}
                                        >
                                            P
                                            {/* Hint dot if punches exist but status is not Present */}
                                            {row.status !== 'PRESENT' && row.status !== 'LATE' && row.punch_records && row.punch_records !== '-' && (
                                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 border-2 border-white rounded-full animate-pulse"></span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'ABSENT')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${row.status === 'ABSENT' ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-600'}`}
                                            title="Absent"
                                        >A</button>

                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'ON_LEAVE')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${row.status === 'ON_LEAVE' || row.status === 'HALF_DAY' ? 'bg-orange-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-orange-100 hover:text-orange-600'}`}
                                            title={row.status === 'HALF_DAY' ? 'Half Day Leave' : (row.status === 'ON_LEAVE' ? 'Full Day Leave' : 'Mark Leave')}
                                        >
                                            {row.status === 'HALF_DAY' ? 'HL' : 'L'}
                                        </button>

                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'ON_DUTY')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${row.status === 'ON_DUTY' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-blue-100 hover:text-blue-600'}`}
                                            title="On Duty"
                                        >OD</button>

                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'PERMISSION')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${row.status === 'PERMISSION' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-purple-100 hover:text-purple-600'}`}
                                            title="Permission"
                                        >PER</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <AnimatePresence>
                {showBulkConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 border border-blue-100"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-4">
                                    <FiInfo size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Bulk Update Status?</h3>
                                <p className="text-gray-500 mb-6 text-sm">
                                    Are you sure you want to mark <b>{selectedIds.length} users</b> as <b>{bulkStatus}</b>? This will update their attendance records for the selected date.
                                </p>
                                <div className="flex gap-3 w-full">
                                    <button
                                        onClick={() => setShowBulkConfirm(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmBulkAction}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md hover:shadow-lg transition"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

// --- Utility: Icon Helper ---
const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    // Handle "2026-01-20" or full ISO
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

const AttendanceDashboard = () => {
    const { user } = useUser();
    const isHROrAdmin = [1, 9, 11].includes(Number(user?.role_id));

    // VIEW STATE: 'dashboard' | 'report' | 'register'
    const [currentView, setCurrentView] = useState('dashboard');


    const [dashboardDate, setDashboardDate] = useState(new Date().toISOString().split('T')[0]);
    const [registerDate, setRegisterDate] = useState(new Date().toISOString().split('T')[0]);
    const punchMonitorRef = React.useRef(null);
    const [reportFilters, setReportFilters] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    // Main filters for stats (now used for dashboard overview)
    const [filters, setFilters] = useState({
        status: 'All',
        designation: 'All',
        facultyName: 'All'
    });

    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState([]);
    const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, on_leave: 0 });
    const [reportStats, setReportStats] = useState({ working_days: 0, leaves: 0, holidays: 0, halfdays: 0, lops: 0 });

    // Detailed Report State
    const [reportData, setReportData] = useState([]);
    const [reportTotal, setReportTotal] = useState(0);
    const [rPage, setRPage] = useState(1);
    const [rLimit, setRLimit] = useState(10);
    const [rSearch, setRSearch] = useState('');
    const [rStatus, setRStatus] = useState('All');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [consolidatedData, setConsolidatedData] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(null); // { id, name } for drill down
    const [consolidatedLoading, setConsolidatedLoading] = useState(false);
    const [consolidatedSummary, setConsolidatedSummary] = useState(null);
    const [gradeFilter, setGradeFilter] = useState('All');

    // Consolidated Pagination State
    const [consolidatedPage, setConsolidatedPage] = useState(1);
    const [consolidatedLimit, setConsolidatedLimit] = useState(25);

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('EDIT'); // 'EDIT' | 'VIEW' | 'EDIT_PUNCH'
    const [modalData, setModalData] = useState(null); // { id, user_id, date, status, name, ...details }
    const [manualReason, setManualReason] = useState('');
    const [editCheckIn, setEditCheckIn] = useState('');
    const [editCheckOut, setEditCheckOut] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Status Dates Popup State
    const [statusDatesPopup, setStatusDatesPopup] = useState(null); // { userId, userName, status, dates }
    const [statusDatesLoading, setStatusDatesLoading] = useState(false);

    // Load Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { ...filters, startDate: dashboardDate, endDate: dashboardDate };
            const [statsRes, listRes, reportRes] = await Promise.all([
                api.get('/attendance/stats', { params }),
                api.get('/attendance/data', { params }),
                api.get('/attendance/report-stats', { params })
            ]);

            if (statsRes.data.status) setStats(statsRes.data.data.overview || {});
            if (listRes.data.status) setAttendanceData(listRes.data.data);
            if (reportRes.data.status) setReportStats(reportRes.data.data);

        } catch (error) { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, [dashboardDate, filters]);

    const handleMonthChange = (val) => {
        if (!val) return;
        const [year, month] = val.split('-');
        const start = `${year}-${month}-01`;
        const end = new Date(year, parseInt(month), 0).toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
        if (currentView === 'report') setRPage(1);
    };

    const shiftMonth = (direction) => {
        const [year, month] = filters.startDate.split('-').map(Number);
        const start = new Date(year, month - 1 + (direction === 'prev' ? -1 : 1), 1);
        const y = start.getFullYear();
        const m = String(start.getMonth() + 1).padStart(2, '0');
        const startDate = `${y}-${m}-01`;
        const endDate = new Date(y, start.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters(prev => ({ ...prev, startDate, endDate }));
        if (currentView === 'report') setRPage(1);
    };

    const shiftDateRange = (direction) => {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const shiftDays = (diffDays === 0 ? 1 : diffDays + 1) * (direction === 'prev' ? -1 : 1);
        start.setDate(start.getDate() + shiftDays);
        end.setDate(end.getDate() + shiftDays);
        setFilters(prev => ({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        }));
        if (currentView === 'report') setRPage(1);
    };

    // Debounce Search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(rSearch);
            setConsolidatedPage(1); // Reset page only when search actually commits
        }, 800);
        return () => clearTimeout(handler);
    }, [rSearch]);

    // Fetch Consolidated Report (Employee-wise)
    const fetchConsolidatedReport = async () => {
        if (currentView !== 'report' || selectedEmp) return;
        setConsolidatedLoading(true);
        try {
            const res = await api.get('/attendance/consolidated-report', {
                params: {
                    startDate: reportFilters.startDate,
                    endDate: reportFilters.endDate,
                    search: debouncedSearch,
                    grade: gradeFilter
                }
            });
            if (res.data.status) {
                setConsolidatedData(res.data.data);
                setConsolidatedSummary(res.data.summary);
            }
        } catch (e) { toast.error('Failed to load summary report'); }
        finally { setConsolidatedLoading(false); }
    };

    useEffect(() => { fetchConsolidatedReport(); }, [currentView, reportFilters, debouncedSearch, selectedEmp, gradeFilter]);


    // Fetch Detailed Report logic (Now used for drill-down or standard list)
    useEffect(() => {
        const fetchDetailedReport = async () => {
            // Determine active view context
            let start = registerDate;
            let end = registerDate;
            let isReportContext = false;

            if (currentView === 'dashboard') {
                start = dashboardDate;
                end = dashboardDate;
            } else if (currentView === 'report' && selectedEmp) {
                start = reportFilters.startDate;
                end = reportFilters.endDate;
                isReportContext = true;
            } else if (currentView === 'register') {
                start = registerDate;
                end = registerDate;
            } else {
                // Return if not in a valid context for this fetch
                return;
            }

            // In reports, only fetch if emp is selected. In others, always fetch if view is active.
            if (currentView === 'report' && !selectedEmp) return;

            setReportLoading(true);
            try {
                const res = await api.get('/attendance/detailed-report', {
                    params: {
                        startDate: start,
                        endDate: end,
                        page: rPage,
                        limit: rLimit,
                        search: isReportContext ? '' : debouncedSearch,
                        status: isReportContext ? 'All' : rStatus,
                        user_id: selectedEmp ? selectedEmp.id : null
                    }
                });
                if (res.data.status) {
                    setReportData(res.data.data);
                    setReportTotal(res.data.total);
                }
            } catch (e) { toast.error('Failed to load logs'); }
            finally { setReportLoading(false); }
        };
        fetchDetailedReport();
    }, [currentView, reportFilters, registerDate, dashboardDate, rPage, rLimit, debouncedSearch, rStatus, selectedEmp]);


    // Recalculate
    const handleRecalculate = async () => {
        const t = toast.loading('Calculating...');

        let start = dashboardDate;
        let end = dashboardDate;

        if (currentView === 'report') {
            start = reportFilters.startDate;
            end = reportFilters.endDate;
        } else if (currentView === 'register') {
            start = registerDate;
            end = registerDate;
        }

        try {
            await api.post('/attendance/recalculate', {
                start_date: start,
                end_date: end
            });
            toast.success('Synced successfully', { id: t });

            // Refresh logic based on view
            setRefreshTrigger(prev => prev + 1); // Triggers RegisterView
            fetchData(); // Triggers Dashboard View
        } catch (e) { toast.error('Failed to sync', { id: t }); }
    };

    // Manual Update Handler (Popup)
    const handleManualClick = (row, targetStatus) => {
        if (row.status === targetStatus) {
            setModalData(row);
            setModalMode('VIEW');
            setModalOpen(true);
        } else {
            setModalData({ ...row, newStatus: targetStatus });
            setManualReason('');
            setEditCheckIn('');
            setEditCheckOut('');
            setModalMode('EDIT');
            setModalOpen(true);
        }
    };

    // Submit Manual Update
    const submitManualUpdate = async () => {
        if (!manualReason.trim()) {
            toast.error('Please enter a reason');
            return;
        }
        const t = toast.loading('Updating...');
        try {
            const payload = {
                id: modalData.id,
                user_id: modalData.user_id,
                date: modalData.attendance_date || modalData.date, // handle both keys
                status: modalData.newStatus || modalData.status,
                reason: manualReason,
                permission_start_time: modalData.permission_start_time,
                permission_end_time: modalData.permission_end_time
            };

            if (modalMode === 'EDIT_PUNCH') {
                payload.check_in_time = editCheckIn;
                payload.check_out_time = editCheckOut;
            }

            if (modalData.newStatus === 'HALF_DAY') {
                // For Half Day, store the leave window in permission fields to preserve biometric data
                payload.permission_start_time = editCheckIn || '09:00';
                payload.permission_end_time = editCheckOut || '14:00';
            }

            await api.post('/attendance/update-status', payload);
            toast.success('Updated successfully', { id: t });
            setModalOpen(false);
            setRefreshTrigger(prev => prev + 1);
            fetchData();
        } catch (e) {
            toast.error('Update Failed', { id: t });
        }
    };

    // Handle Status Badge Click (Late, LOP, Leave, Absent)
    const handleStatusClick = async (userId, userName, status) => {
        setStatusDatesLoading(true);
        setStatusDatesPopup({ userId, userName, status, dates: [] });

        try {
            const res = await api.get('/attendance/status-dates', {
                params: {
                    user_id: userId,
                    status: status,
                    startDate: reportFilters.startDate,
                    endDate: reportFilters.endDate
                }
            });

            if (res.data.status) {
                setStatusDatesPopup({
                    userId,
                    userName,
                    status,
                    dates: res.data.data
                });
            }
        } catch (e) {
            toast.error('Failed to load dates');
        } finally {
            setStatusDatesLoading(false);
        }
    };

    const handleExport = (type) => {
        if (!selectedEmp) {
            // EXPORT CONSOLIDATED
            if (type === 'EXCEL') {
                const data = consolidatedData.map((r, i) => ({
                    'S.No': i + 1,
                    'Employee': r.name,
                    'Designation': r.designation,
                    'Working Days': r.working_days,
                    'Present': r.present,
                    'Late': r.late,
                    'Half Day': r.half_day,
                    'Leave': r.leave,
                    'Absent': r.absent,
                    'Atten %': r.percentage + '%',
                    'Status': r.status_label
                }));
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Attendance_Summary");
                XLSX.writeFile(wb, `Attendance_Summary_${reportFilters.startDate}_to_${reportFilters.endDate}.xlsx`);
            } else {
                const doc = new jsPDF('landscape');
                doc.setFontSize(14);
                doc.setTextColor(216, 0, 0);
                doc.text("Attendance Summary Report", 14, 15);
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                doc.text(`Period: ${reportFilters.startDate} to ${reportFilters.endDate}`, 14, 22);

                autoTable(doc, {
                    startY: 28,
                    head: [['No.', 'Employee', 'Designation', 'Work Days', 'Pres', 'Late', 'Half', 'Leave', 'Abs', '%']],
                    body: consolidatedData.map((r, i) => [
                        i + 1, r.name, r.designation, r.working_days, r.present, r.late, r.half_day, r.leave, r.absent, r.percentage + '%'
                    ]),
                    headStyles: { fillColor: [243, 244, 246], textColor: [75, 85, 99], fontStyle: 'bold' }
                });
                doc.save(`Attendance_Summary_${reportFilters.startDate}.pdf`);
            }
        } else {
            // EXPORT DETAILED DRILL-DOWN
            if (type === 'EXCEL') {
                const data = reportData.map((r, i) => ({
                    'Date': r.attendance_date,
                    'Check In': r.check_in,
                    'Check Out': r.check_out,
                    'Late': calculateLate(r.check_in),
                    'Status': r.status,
                    'Punch Records': r.punch_records
                }));
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Daily_Logs");
                XLSX.writeFile(wb, `Logs_${selectedEmp.name}_${reportFilters.startDate}.xlsx`);
            } else {
                const doc = new jsPDF();
                doc.setFontSize(14);
                doc.text(`Daily Logs: ${selectedEmp.name}`, 14, 15);
                doc.setFontSize(10);
                doc.text(`Period: ${reportFilters.startDate} to ${reportFilters.endDate}`, 14, 22);

                autoTable(doc, {
                    startY: 28,
                    head: [['Date', 'In', 'Out', 'Late', 'Status', 'Punches']],
                    body: reportData.map(r => [
                        r.attendance_date, r.check_in, r.check_out, calculateLate(r.check_in), r.status, r.punch_records
                    ]),
                });
                doc.save(`Logs_${selectedEmp.name}.pdf`);
            }
        }
    };

    const handlePunchEdit = (row) => {
        if (!row) return;

        // Extremely robust time parser for HTML <input type="time">
        const to24h = (timeStr) => {
            if (!timeStr || typeof timeStr !== 'string' || timeStr === '-' || timeStr === '00:00:00') return '';

            try {
                let h, m;
                const clean = timeStr.trim();

                // Case 1: "09:30 AM" or "9:30 AM"
                if (clean.toLowerCase().includes('m')) {
                    const parts = clean.split(/\s+/);
                    if (parts.length < 2) return '';
                    const timePart = parts[0];
                    const period = parts[1].toUpperCase();

                    const [hStr, mStr] = timePart.split(':');
                    h = parseInt(hStr);
                    m = mStr || '00';

                    if (period === 'PM' && h !== 12) h += 12;
                    if (period === 'AM' && h === 12) h = 0;
                } else {
                    // Case 2: "09:30:00" or "09:30"
                    const parts = clean.split(':');
                    h = parseInt(parts[0]);
                    m = parts[1] || '00';
                }

                if (isNaN(h)) return '';
                return `${h.toString().padStart(2, '0')}:${m.toString().substring(0, 2).padStart(2, '0')}`;
            } catch (e) {
                console.error("Time Parse Error:", e);
                return '';
            }
        };

        // 1. Primary Extraction (Try all common keys)
        let cIn = row.checkIn || row.check_in || row.first_punch || '';
        let cOut = row.checkOut || row.check_out || row.last_punch || '';

        // 2. Fallback: Parse from punch_records string ("09:00 AM, 06:00 PM")
        if ((!cIn || cIn === '-') && row.punch_records && row.punch_records !== '-') {
            const records = row.punch_records.split(/[,|]/).map(s => s.trim()).filter(Boolean);
            if (records.length > 0) {
                if (!cIn || cIn === '-') cIn = records[0];
                if ((!cOut || cOut === '-') && records.length > 1) cOut = records[records.length - 1];
            }
        }

        setEditCheckIn(to24h(cIn));
        setEditCheckOut(to24h(cOut));
        setModalData(row);
        setManualReason(row.override_reason || '');
        setModalMode('EDIT_PUNCH');
        setModalOpen(true);
    };

    // --- SHARED HELPERS ---
    const parseTime = (timeStr) => {
        if (!timeStr || timeStr === '-' || !timeStr.trim()) return null;
        try {
            const [time, modifier] = timeStr.trim().split(' ');
            let [hours, minutes] = time.split(':').map(Number);
            if (modifier === 'PM' && hours < 12) hours += 12;
            if (modifier === 'AM' && hours === 12) hours = 0;
            return { hours, minutes, totalMinutes: hours * 60 + minutes };
        } catch (e) { return null; }
    };

    const formatDuration = (totalMins) => {
        if (totalMins <= 0) return '-';
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        if (h > 0) return `${h}.${Math.floor((m / 60) * 100)} Hrs`;
        return `${m} Min`;
    };

    const calculateLate = (checkInStr) => {
        if (!checkInStr) return '-';
        const inTime = parseTime(checkInStr);
        if (!inTime) return '-';
        const startMins = 9 * 60 + 5; // 9:05 AM
        const diff = inTime.totalMinutes - startMins;
        return diff > 0 ? formatDuration(diff) : '-';
    };

    const calculateOvertime = (checkOutStr) => {
        if (!checkOutStr) return '-';
        const outTime = parseTime(checkOutStr);
        if (!outTime) return '-';
        const endMins = 18 * 60; // 6:00 PM
        const diff = outTime.totalMinutes - endMins;
        return diff > 0 ? formatDuration(diff) : '-';
    };

    const calculateProduction = (checkInStr, checkOutStr) => {
        if (!checkInStr || !checkOutStr) return '-';
        const inTime = parseTime(checkInStr);
        const outTime = parseTime(checkOutStr);
        if (!inTime || !outTime) return '-';
        const diff = outTime.totalMinutes - inTime.totalMinutes;
        return diff > 0 ? formatDuration(diff) : '-';
    };

    // 1. DASHBOARD VIEW
    const DashboardView = () => {
        // State
        const [dPage, setDPage] = useState(1);
        const [dLimit, setDLimit] = useState(10);
        const [dSearch, setDSearch] = useState('');
        const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

        // Filter Handlers
        const handleDashboardDateChange = (val) => {
            setDashboardDate(val);
        };

        const shiftDashboardDate = (direction) => {
            const d = new Date(dashboardDate);
            d.setDate(d.getDate() + (direction === 'prev' ? -1 : 1));
            setDashboardDate(d.toISOString().split('T')[0]);
        };

        // Exports
        const handleExportExcel = () => {
            const dataToExport = attendanceData.map(row => ({
                Employee: row.name,
                Date: row.date,
                CheckIn: row.checkIn,
                Status: row.status,
                CheckOut: row.checkOut,
                Late: calculateLate(row.checkIn),
                Overtime: calculateOvertime(row.checkOut),
                ProductionHours: calculateProduction(row.checkIn, row.checkOut)
            }));
            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
            XLSX.writeFile(workbook, "Attendance_Log.xlsx");
        };

        const handleExportPDF = () => {
            const doc = new jsPDF();
            doc.text("Attendance Logs", 14, 15);
            autoTable(doc, {
                startY: 20,
                head: [['Employee', 'Date', 'In', 'Status', 'Out', 'Late', 'OT', 'Prod']],
                body: attendanceData.map(row => [
                    row.name, row.date, row.checkIn, row.status, row.checkOut,
                    calculateLate(row.checkIn), calculateOvertime(row.checkOut), calculateProduction(row.checkIn, row.checkOut)
                ]),
            });
            doc.save("Attendance_Logs.pdf");
        };

        const handleExportPunchReport = () => {
            const doc = new jsPDF();

            // Header: Company Name (Center, Red)
            doc.setFontSize(14);
            doc.setTextColor(216, 0, 0); // Red
            doc.text("eNova Software & Hardware Solutions", 105, 15, { align: 'center' });

            // Title: Employee Punch Monitor (Center, Black)
            doc.setTextColor(0, 0, 0);
            doc.text("Employee Punch Monitor", 105, 22, { align: 'center' });

            doc.setFontSize(10);
            const dateStr = formatDateDDMMYYYY(dashboardDate);
            doc.text(`Date: ${dateStr}`, 14, 30);

            // Sort data exactly as processed for the Export
            const exportData = [...processedData];

            autoTable(doc, {
                startY: 35,
                headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontSize: 8 },
                bodyStyles: { fontSize: 8 },
                head: [['S.No', 'Name', 'Check In', 'Punch Records', 'Status']],
                body: exportData.map((row, i) => [
                    i + 1,
                    row.name,
                    row.checkIn,
                    row.punch_records || row.checkIn,
                    row.status?.replace(/_/g, ' ')
                ]),
                didParseCell: (data) => {
                    // Conditional Styling
                    if (data.section === 'body' && data.column.index === 4) {
                        const s = data.cell.raw?.toUpperCase();
                        if (s?.includes('LATE') || s?.includes('YESTERDAY')) data.cell.styles.fillColor = [173, 216, 230];
                        if (s?.includes('ABSENT') || s?.includes('NOT PRESENT')) data.cell.styles.fillColor = [255, 99, 71];
                        if (s?.includes('PRESENT')) data.cell.styles.fillColor = [220, 220, 220];
                    }
                }
            });
            doc.save(`Punch_Report_${dashboardDate}.pdf`);
        };

        const handleExportImage = async () => {
            const t = toast.loading('Generating image...');
            try {
                // We create a temporary hidden div with the full table to capture it
                const element = document.getElementById('punch-monitor-export-container');
                if (!element) throw new Error('Export container not found');

                element.style.display = 'block';
                const dataUrl = await toJpeg(element, {
                    quality: 1,
                    backgroundColor: '#ffffff',
                    fontEmbedCSS: '', // Fix SecurityError: Failed to read 'cssRules'
                    cacheBust: true
                });
                element.style.display = 'none';

                saveAs(dataUrl, `Punch_Monitor_${dashboardDate}.jpg`);
                toast.success('Image exported successfully', { id: t });
            } catch (e) {
                console.error(e);
                toast.error('Failed to export image', { id: t });
            }
        };

        const requestSort = (key) => {
            let direction = 'asc';
            if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
            setSortConfig({ key, direction });
        };

        // Data Processing
        const processedData = React.useMemo(() => {
            let stats = attendanceData.filter(row =>
                row.name.toLowerCase().includes(dSearch.toLowerCase()) ||
                (row.employeeId && row.employeeId.toLowerCase().includes(dSearch.toLowerCase()))
            );

            // Enhance data with calculated fields for sorting
            stats = stats.map(row => ({
                ...row,
                lateVal: calculateLate(row.checkIn),
                otVal: calculateOvertime(row.checkOut),
                prodVal: calculateProduction(row.checkIn, row.checkOut)
            }));

            const getStatusPriority = (status) => {
                const s = (status || '').toUpperCase();
                if (s.includes('PRESENT')) return 1;
                if (s.includes('HALF')) return 1.5;
                if (s.includes('LATE') && !s.includes('YESTERDAY')) return 2;
                if (s.includes('WENT_LATE_YESTERDAY') || s.includes('WENT LATE')) return 3;
                if (s.includes('DUTY')) return 4;
                if (s.includes('LEAVE')) return 5;
                if (s.includes('PERMISSION')) return 6;
                if (s.includes('HOLIDAY')) return 7;
                if (s.includes('ABSENT')) return 8;
                return 9;
            };

            const parseCheckIn = (timeStr) => {
                if (!timeStr || timeStr === '-') return '99:99';
                // Convert "09:30 AM" to "09:30" or "21:30" for sorting
                const parsed = parseTime(timeStr);
                if (!parsed) return '99:99';
                return `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes.toString().padStart(2, '0')}`;
            };

            stats.sort((a, b) => {
                // If user requested specific sort
                if (sortConfig.key) {
                    let aVal = a[sortConfig.key];
                    let bVal = b[sortConfig.key];
                    if (sortConfig.key === 'checkIn' || sortConfig.key === 'checkOut') {
                        aVal = aVal !== '-' ? aVal : '';
                        bVal = bVal !== '-' ? bVal : '';
                    }
                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }

                // Default Sort: Status Priority -> Entry Time
                const orderA = getStatusPriority(a.status);
                const orderB = getStatusPriority(b.status);

                if (orderA !== orderB) return orderA - orderB;

                // Within same status, sort by check-in time
                const timeA = parseCheckIn(a.checkIn);
                const timeB = parseCheckIn(b.checkIn);
                return timeA.localeCompare(timeB);
            });

            return stats;
        }, [attendanceData, dSearch, sortConfig]);

        const totalPages = Math.ceil(processedData.length / dLimit);
        const paginatedData = processedData.slice((dPage - 1) * dLimit, dPage * dLimit);

        const getPageNumbers = () => {
            const pages = [];
            let start = Math.max(1, dPage - 2);
            let end = Math.min(totalPages, start + 4);
            if (end - start < 4) start = Math.max(1, end - 4);
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        };

        const SortIcon = ({ column }) => {
            if (sortConfig.key !== column) return <span className="text-gray-300 ml-1"><FiChevronUp className="inline w-3 h-3 opacity-0 group-hover:opacity-50" /></span>;
            return sortConfig.direction === 'asc' ? <FiChevronUp className="inline ml-1 text-blue-600" /> : <FiChevronDown className="inline ml-1 text-blue-600" />;
        };

        return (
            <div className="space-y-6 animate-fade-in-up">
                {/* Stat Cards - Keep as is */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="p-3 bg-green-50 text-green-600 rounded-full"><FiCheckCircle size={24} /></div>
                        <div><p className="text-xs text-gray-500">Present</p><p className="text-xl font-bold">{stats.present}</p></div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="p-3 bg-red-50 text-red-600 rounded-full"><FiXCircle size={24} /></div>
                        <div><p className="text-xs text-gray-500">Absent</p><p className="text-xl font-bold">{stats.absent}</p></div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="p-3 bg-yellow-50 text-yellow-600 rounded-full"><FiClock size={24} /></div>
                        <div><p className="text-xs text-gray-500">Late</p><p className="text-xl font-bold">{stats.late}</p></div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                        {isHROrAdmin ? (
                            <>
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-full"><FiCalendar size={24} /></div>
                                <div><p className="text-xs text-gray-500">On Leave</p><p className="text-xl font-bold">{stats.on_leave}</p></div>
                            </>
                        ) : (
                            <>
                                <div className="p-3 bg-orange-50 text-orange-600 rounded-full"><FiAlertCircle size={24} /></div>
                                <div><p className="text-xs text-gray-500">Late (This Month)</p><p className="text-xl font-bold">{stats.current_month_late || 0}</p></div>
                            </>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                    {/* Toolbar */}
                    <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <select
                                    value={dLimit}
                                    onChange={(e) => { setDLimit(Number(e.target.value)); setDPage(1); }}
                                    className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 py-2 pl-3 pr-8 outline-none cursor-pointer hover:bg-white transition-colors"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={999999}>All</option>
                                </select>
                            </div>
                            <span className="text-sm font-medium text-gray-500">Entries</span>
                        </div>

                        <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                            {/* Single Date Navigation */}
                            <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-lg border border-gray-200">
                                <button onClick={() => shiftDashboardDate('prev')} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600" title="Previous Day">
                                    <FiChevronLeft />
                                </button>
                                <input
                                    type="date"
                                    value={dashboardDate}
                                    onChange={e => handleDashboardDateChange(e.target.value)}
                                    className="border-none bg-transparent text-sm font-black w-32 focus:ring-0 p-1 text-center"
                                />
                                <button onClick={() => shiftDashboardDate('next')} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600" title="Next Day">
                                    <FiChevronRight />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={dSearch}
                                    onChange={e => { setDSearch(e.target.value); setDPage(1); }}
                                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors text-sm w-40"
                                />
                            </div>

                            <div className="flex gap-2">
                                <button onClick={handleExportExcel} className="p-2 text-green-600 rounded hover:bg-green-50 transition-colors shadow-sm border border-gray-200" title="Export Excel"><RiFileExcelFill size={20} /></button>
                                <button onClick={handleExportPDF} className="p-2 text-red-600 rounded hover:bg-red-50 transition-colors shadow-sm border border-gray-200" title="Export PDF"><RiFilePdf2Fill size={20} /></button>
                                <button onClick={handleExportPunchReport} className="flex items-center gap-1 p-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors shadow-sm text-xs font-bold" title="Daily Punch Report PDF">
                                    <RiFilePdf2Fill size={14} /> PDF
                                </button>
                                <button onClick={handleExportImage} className="flex items-center gap-1 p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors shadow-sm text-xs font-bold" title="Daily Punch Report Image">
                                    <FiDownload size={14} /> Image
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-100/50 sticky top-0 z-10 text-gray-600 text-xs font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('name')}>Name <SortIcon column="name" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('date')}>Date <SortIcon column="date" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('checkIn')}>Check In <SortIcon column="checkIn" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('status')}>Status <SortIcon column="status" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('checkOut')}>Check Out <SortIcon column="checkOut" /></th>
                                    <th className="px-6 py-4">Late</th>
                                    <th className="px-6 py-4">Overtime</th>
                                    <th className="px-6 py-4">Production Hours</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {paginatedData.length > 0 ? paginatedData.map((row, i) => (
                                    <tr key={i} className="bg-white hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {row.img ? <img src={row.img} alt="" className="w-9 h-9 rounded-full object-cover" /> :
                                                    <div className="w-9 h-9 rounded-full bg-gray-800 text-white flex items-center justify-center font-bold text-xs">
                                                        {row.name.charAt(0)}
                                                    </div>}
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-800">{row.name}</span>
                                                    <span className="text-xs text-gray-500">{row.designation || 'Employee'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-600">{formatDateDDMMYYYY(row.date)}</td>
                                        <td className="px-6 py-4 font-medium text-gray-600">{row.checkIn}</td>
                                        <td className="px-6 py-4">
                                            {/* Reverted Status Badge Style as requested */}
                                            <span className={`px-2.5 py-1 rounded text-xs font-bold ${row.color}`}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-600">{row.checkOut}</td>

                                        {/* Calculated Columns */}
                                        <td className="px-6 py-4 text-gray-500">{row.lateVal}</td>
                                        <td className="px-6 py-4 text-gray-500">{row.otVal}</td>

                                        <td className="px-6 py-4">
                                            {/* Production Badge Style */}
                                            {row.prodVal !== '-' ? (
                                                <span className={`px-2.5 py-1 rounded-md text-xs font-bold text-white shadow-sm flex items-center gap-1 w-fit
                                                    ${parseFloat(row.prodVal) >= 8 ? 'bg-green-500' : 'bg-red-500'}`}>
                                                    <FiClock size={12} /> {row.prodVal}
                                                </span>
                                            ) : <span className="text-gray-400">-</span>}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">No records found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {totalPages > 1 && (
                        <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 bg-gray-50 gap-4">
                            <div className="text-xs text-gray-500 font-medium">
                                Showing {Math.min((dPage - 1) * dLimit + 1, processedData.length)} to {Math.min(dPage * dLimit, processedData.length)} of {processedData.length} entries
                            </div>
                            <div className="flex items-center gap-2">
                                <button disabled={dPage === 1} onClick={() => setDPage(p => p - 1)} className="p-2 border rounded hover:bg-white transition-colors disabled:opacity-50"><FiChevronLeft size={14} /></button>
                                <div className="flex gap-1">{getPageNumbers().map(p => (<button key={p} onClick={() => setDPage(p)} className={`w-7 h-7 flex items-center justify-center rounded text-xs font-bold transition-colors ${dPage === p ? 'bg-[#C30010] text-white' : 'hover:bg-gray-200 text-gray-600'}`}>{p}</button>))}</div>
                                <button disabled={dPage === totalPages} onClick={() => setDPage(p => p + 1)} className="p-2 border rounded hover:bg-white transition-colors disabled:opacity-50"><FiChevronRight size={14} /></button>
                            </div>
                        </div>
                    )}
                    {/* Hidden Export Container for Image Capture */}
                    <div id="punch-monitor-export-container" className="fixed top-0 left-0 bg-white" style={{ display: 'none', width: '1200px', zIndex: -100 }}>
                        <div className="p-8">
                            <h2 className="text-center text-2xl font-black text-gray-800 mb-1">Employee Punch Monitor</h2>
                            <div className="flex justify-between items-center mb-4 px-2">
                                <span className="text-sm font-bold border-b-2 border-gray-800 pb-1">Date: {formatDateDDMMYYYY(dashboardDate)}</span>
                            </div>

                            <div className="flex gap-4 items-start">
                                {[0, 1].map(halfIndex => {
                                    const midPoint = Math.ceil(processedData.length / 2);
                                    const dataToShow = halfIndex === 0
                                        ? processedData.slice(0, midPoint)
                                        : processedData.slice(midPoint);
                                    const startSNo = halfIndex === 0 ? 0 : midPoint;

                                    if (dataToShow.length === 0) return null;

                                    return (
                                        <div key={halfIndex} className="flex-1">
                                            <table className="w-full border-collapse border border-gray-800 text-[10px]">
                                                <thead>
                                                    <tr className="bg-gray-200">
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-10">SNo.</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left">Name</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-16">In</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left">Punch Records</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-20">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dataToShow.map((row, i) => {
                                                        const s = row.status?.toUpperCase() || '';
                                                        let displayStatus = row.status?.replace(/_/g, ' ');
                                                        let statusBg = 'bg-white';

                                                        if (s.includes('PRESENT') || s.includes('HALF_DAY')) statusBg = 'bg-gray-300';
                                                        if (s.includes('LATE') || s.includes('YESTERDAY')) statusBg = 'bg-blue-200';
                                                        if (s.includes('ABSENT') || s.includes('NOT PRESENT') || s === 'NOT MARKED') {
                                                            statusBg = 'bg-red-600 text-white';
                                                            displayStatus = 'Not Present';
                                                        }

                                                        return (
                                                            <tr key={i} className="border border-gray-800">
                                                                <td className="border border-gray-800 px-2 py-1 text-center font-bold bg-gray-50">{startSNo + i + 1}</td>
                                                                <td className="border border-gray-800 px-2 py-1 font-medium">{row.name}</td>
                                                                <td className="border border-gray-800 px-2 py-1 whitespace-nowrap">{row.checkIn === '-' ? '' : row.checkIn?.replace(' AM', '').replace(' PM', '')}</td>
                                                                <td className="border border-gray-800 px-2 py-1">{row.punch_records?.replace(/ AM/g, '').replace(/ PM/g, '')}</td>
                                                                <td className={`border border-gray-800 px-1 py-1 text-center font-bold text-[9px] ${statusBg}`}>
                                                                    {displayStatus}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- SORTING & PAGINATION LOGIC (Promoted from ReportView) ---
    const [consolidatedSort, setConsolidatedSort] = React.useState({ key: null, direction: 'asc' });

    // Summary Card Component (Inlined)
    const SummaryCard = ({ title, value, label, icon: Icon, colorClass, bgColor }) => (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start justify-between hover:shadow-lg transition-all border-b-4" style={{ borderColor: bgColor.replace('bg-', '') }}>
            <div className="space-y-1">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{title}</p>
                <h3 className="text-3xl font-black text-gray-800">{value}</h3>
                <p className="text-[11px] font-bold text-gray-500">{label}</p>
            </div>
            <div className={`p-3 rounded-xl ${bgColor} ${colorClass} shadow-inner`}>
                <Icon size={24} />
            </div>
        </div>
    );

    const requestSort = (key) => {
        let direction = 'asc';
        if (consolidatedSort.key === key && consolidatedSort.direction === 'asc') {
            direction = 'desc';
        }
        setConsolidatedSort({ key, direction });
        setConsolidatedPage(1);
    };

    const SortIcon = ({ column }) => {
        if (consolidatedSort.key !== column) {
            return <span className="text-gray-300 ml-1"><FiChevronUp className="inline w-3 h-3 opacity-0 group-hover:opacity-50" /></span>;
        }
        return consolidatedSort.direction === 'asc' ?
            <FiChevronUp className="inline ml-1 text-[#C30010]" /> :
            <FiChevronDown className="inline ml-1 text-[#C30010]" />;
    };

    const sortedConsolidatedData = React.useMemo(() => {
        let sorted = [...consolidatedData];
        if (consolidatedSort.key) {
            sorted.sort((a, b) => {
                let aVal = a[consolidatedSort.key];
                let bVal = b[consolidatedSort.key];
                if (typeof aVal === 'number' && typeof bVal === 'number') return consolidatedSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
                if (aVal < bVal) return consolidatedSort.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return consolidatedSort.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sorted;
    }, [consolidatedData, consolidatedSort]);

    const totalConsolidatedPages = Math.ceil(sortedConsolidatedData.length / consolidatedLimit);
    const paginatedConsolidatedData = sortedConsolidatedData.slice(
        (consolidatedPage - 1) * consolidatedLimit,
        consolidatedPage * consolidatedLimit
    );

    const getConsolidatedPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, consolidatedPage - 2);
        let end = Math.min(totalConsolidatedPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    // Report Date Range Handlers
    const handleReportDateChange = (field, value) => {
        setReportFilters(prev => ({ ...prev, [field]: value }));
        setRPage(1);
        setConsolidatedPage(1);
    };

    const shiftReportRange = (direction) => {
        const start = new Date(reportFilters.startDate);
        const end = new Date(reportFilters.endDate);
        const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
        const shiftDays = (diffDays === 0 ? 1 : diffDays + 1) * (direction === 'prev' ? -1 : 1);
        start.setDate(start.getDate() + shiftDays);
        end.setDate(end.getDate() + shiftDays);
        setReportFilters({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        });
        setRPage(1);
    };

    // 2. REPORT VIEW CONTENT
    const ReportViewContent = (
        <div className="space-y-6 animate-fade-in-up">
            {/* ADVANCED HR SUMMARY CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <SummaryCard
                    title="Workforce Size"
                    value={consolidatedSummary?.total_employees || 0}
                    label="Total Employees"
                    icon={FiSearch}
                    colorClass="text-blue-600"
                    bgColor="bg-blue-50"
                />
                <SummaryCard
                    title="Policy Period"
                    value={consolidatedSummary?.working_days || 0}
                    label="Working Days"
                    icon={FiCalendar}
                    colorClass="text-orange-600"
                    bgColor="bg-orange-50"
                />
                <SummaryCard
                    title="System Health"
                    value={`${consolidatedSummary?.avg_percentage || 0}%`}
                    label="Avg Attendance"
                    icon={FiTrendingUp}
                    colorClass="text-emerald-600"
                    bgColor="bg-emerald-50"
                />
                <SummaryCard
                    title="Absenteeism"
                    value={consolidatedSummary?.total_absents || 0}
                    label="Total Absents"
                    icon={FiAlertCircle}
                    colorClass="text-amber-600"
                    bgColor="bg-amber-50"
                />
            </div>

            {/* CONSOLIDATED MASTER REPORT */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Enterprise Toolbar */}
                <div className="p-6 border-b border-gray-100 bg-gray-50/30 flex flex-col xl:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white shadow-sm rounded-xl border border-gray-200">
                            <FiFileText className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-gray-800 tracking-tight">Enterprise Attendance Report</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1 text-[10px] font-black bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase">Consolidated</span>
                                <span className="text-xs text-blue-600 font-black tracking-tight uppercase">
                                    {reportFilters.startDate === reportFilters.endDate ?
                                        formatDateDDMMYYYY(reportFilters.startDate) :
                                        `${formatDateDDMMYYYY(reportFilters.startDate)} — ${formatDateDDMMYYYY(reportFilters.endDate)}`}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* High-End Filters */}
                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                        <div className="flex items-center gap-2 bg-white border border-gray-200 p-2 rounded-xl shadow-sm">
                            <span className="text-xs font-bold text-gray-600">Show</span>
                            <select
                                value={consolidatedLimit}
                                onChange={(e) => {
                                    setConsolidatedLimit(Number(e.target.value));
                                    setConsolidatedPage(1);
                                }}
                                className="border-none bg-transparent text-sm font-bold text-gray-700 focus:ring-0 p-0 pr-6"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-xs font-bold text-gray-600">entries</span>
                        </div>

                        <div className="flex items-center gap-1 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm">
                            <button onClick={() => shiftReportRange('prev')} className="p-2 hover:bg-gray-50 rounded-lg transition-all text-gray-500" title="Previous Range"><FiChevronLeft /></button>
                            <input
                                type="date"
                                value={reportFilters.startDate}
                                onChange={e => handleReportDateChange('startDate', e.target.value)}
                                className="border-none bg-transparent text-sm font-bold w-32 focus:ring-0 p-1"
                            />
                            <span className="text-gray-300 font-bold px-1 text-xs">TO</span>
                            <input
                                type="date"
                                value={reportFilters.endDate}
                                onChange={e => handleReportDateChange('endDate', e.target.value)}
                                className="border-none bg-transparent text-sm font-bold w-32 focus:ring-0 p-1"
                            />
                            <button onClick={() => shiftReportRange('next')} className="p-2 hover:bg-gray-50 rounded-lg transition-all text-gray-500" title="Next Range"><FiChevronRight /></button>
                        </div>

                        <select
                            value={gradeFilter}
                            onChange={e => setGradeFilter(e.target.value)}
                            className="bg-white border border-gray-200 p-2.5 rounded-xl text-sm font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                            <option value="All">All Grades</option>
                            <option value="Excellent">Excellent (≥90%)</option>
                            <option value="Good">Good (80-89%)</option>
                            <option value="Average">Average (70-79%)</option>
                            <option value="Poor">Poor (&lt;70%)</option>
                        </select>

                        <div className="relative flex-1 md:flex-none">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search Employee..."
                                value={rSearch}
                                onChange={e => setRSearch(e.target.value)}
                                className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 w-full md:w-64 bg-white shadow-sm font-medium"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={() => handleExport('EXCEL')} className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-black uppercase tracking-widest border border-emerald-100 hover:bg-emerald-100 transition-all shadow-sm">
                                <FiDownload /> Excel
                            </button>
                            <button onClick={() => handleExport('PDF')} className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 text-rose-700 rounded-xl text-xs font-black uppercase tracking-widest border border-rose-100 hover:bg-rose-100 transition-all shadow-sm">
                                <FiFileText /> PDF
                            </button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="bg-gray-100 text-gray-700 sticky top-0 z-20 border-b border-gray-200">
                            <tr className="divide-x divide-gray-200">
                                <th className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center w-16">No.</th>
                                <th onClick={() => requestSort('name')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest min-w-[200px] cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Employee Details <SortIcon column="name" />
                                </th>
                                <th onClick={() => requestSort('working_days')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Work Days <SortIcon column="working_days" />
                                </th>
                                <th onClick={() => requestSort('present')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Present <SortIcon column="present" />
                                </th>
                                <th onClick={() => requestSort('late')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Late <SortIcon column="late" />
                                </th>
                                <th onClick={() => requestSort('lop')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    LOP <SortIcon column="lop" />
                                </th>
                                <th onClick={() => requestSort('leave')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Leave <SortIcon column="leave" />
                                </th>
                                <th onClick={() => requestSort('absent')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Absent <SortIcon column="absent" />
                                </th>
                                <th onClick={() => requestSort('percentage')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Atten % <SortIcon column="percentage" />
                                </th>
                                <th onClick={() => requestSort('grade')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Grade <SortIcon column="grade" />
                                </th>
                                <th className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {consolidatedLoading ? (
                                <tr><td colSpan="11" className="text-center py-20 text-gray-400 font-bold italic animate-pulse">Running architectural aggregation...</td></tr>
                            ) : sortedConsolidatedData.length === 0 ? (
                                <tr><td colSpan="11" className="text-center py-20 text-gray-400 font-bold uppercase tracking-widest">No consolidated data found</td></tr>
                            ) : paginatedConsolidatedData.map((row, i) => (
                                <tr key={i} className={`hover:bg-[#C30010]/5 transition-all group ${row.has_lop ? 'bg-red-50/30' : ''}`}>
                                    <td className="px-6 py-5 text-center text-gray-400 font-mono text-xs">{(consolidatedPage - 1) * consolidatedLimit + i + 1}</td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            {row.img ? <img src={row.img} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white shadow-md" /> :
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-600 text-white flex items-center justify-center font-black text-xs shadow-md">{row.name.charAt(0)}</div>}
                                            <div>
                                                <p className="font-black text-gray-800 text-sm whitespace-nowrap">{row.name}</p>
                                                <p className="text-[10px] text-blue-600 font-black uppercase tracking-tight">{row.designation}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center font-bold text-gray-800">{row.working_days}</td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="text-emerald-700 font-black bg-emerald-50 px-2.5 py-1 rounded text-xs border border-emerald-100">{row.present}</span>
                                            {row.holiday_worked > 0 && (
                                                <button
                                                    onClick={() => handleStatusClick(row.user_id, row.name, 'HOLIDAY_WORKED')}
                                                    className="text-[10px] text-purple-600 font-bold mt-1 bg-purple-50 px-1.5 rounded border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors"
                                                >
                                                    +{row.holiday_worked} OT
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.late > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'LATE')}
                                                className="text-amber-700 font-black bg-amber-50 px-2.5 py-1 rounded text-xs border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                                            >
                                                {row.late}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.lop > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'LOP')}
                                                className="text-red-700 font-black bg-red-100 px-2.5 py-1 rounded text-xs border-2 border-red-200 cursor-pointer hover:bg-red-200 transition-colors"
                                            >
                                                {row.lop}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.leave > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'ON_LEAVE')}
                                                className="text-gray-600 font-bold cursor-pointer hover:text-[#C30010] transition-colors underline"
                                            >
                                                {row.leave}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.absent > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'ABSENT')}
                                                className="text-rose-700 font-black bg-rose-50 px-2.5 py-1 rounded text-xs border border-rose-100 cursor-pointer hover:bg-rose-100 transition-colors"
                                            >
                                                {row.absent}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="font-black text-gray-800 text-sm leading-none">{row.percentage}%</span>
                                            <div className="w-16 bg-gray-100 h-1.5 rounded-full overflow-hidden border border-gray-200">
                                                <div className={`h-full ${parseFloat(row.percentage) >= 90 ? 'bg-emerald-500' : parseFloat(row.percentage) >= 80 ? 'bg-blue-500' : parseFloat(row.percentage) >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${row.percentage}%` }}></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <span className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border-2 ${row.grade_variant}`}>
                                            {row.grade}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <button
                                            onClick={() => {
                                                setSelectedEmp({ id: row.user_id, name: row.name });
                                                setRPage(1); // Reset page for detailed logs
                                            }}
                                            className="px-4 py-2 bg-white text-[#C30010] border-2 border-[#C30010] rounded-xl text-[11px] font-black uppercase tracking-tight hover:bg-[#C30010] hover:text-white transition-all shadow-sm active:scale-95 whitespace-nowrap"
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>


                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Left: Showing info */}
                        <div className="text-sm text-gray-600 font-medium">
                            Showing {Math.min((consolidatedPage - 1) * consolidatedLimit + 1, sortedConsolidatedData.length)} to {Math.min(consolidatedPage * consolidatedLimit, sortedConsolidatedData.length)} of {sortedConsolidatedData.length} entries
                        </div>

                        {/* Right: Page navigation */}
                        {totalConsolidatedPages > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    disabled={consolidatedPage === 1}
                                    onClick={() => setConsolidatedPage(p => p - 1)}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FiChevronLeft size={14} />
                                </button>
                                <div className="flex gap-1">
                                    {getConsolidatedPageNumbers().map(p => (
                                        <button
                                            key={p}
                                            onClick={() => setConsolidatedPage(p)}
                                            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-colors ${consolidatedPage === p
                                                ? 'bg-[#C30010] text-white shadow-md'
                                                : 'hover:bg-gray-200 text-gray-600'
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    disabled={consolidatedPage === totalConsolidatedPages}
                                    onClick={() => setConsolidatedPage(p => p + 1)}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FiChevronRight size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL / OVERLAY FOR DETAILED DRILL-DOWN */}
            {selectedEmp && (
                <div className="fixed inset-0 z-[100] bg-gray-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl h-4/5 overflow-hidden flex flex-col border border-white/20 animate-scale-in">
                        <div className="p-6 bg-gray-100 border-b border-gray-200 text-gray-800 flex justify-between items-center shadow-sm">
                            <div className="flex items-center gap-4">
                                <button onClick={() => setSelectedEmp(null)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><FiArrowLeft size={24} /></button>
                                <div>
                                    <h2 className="text-xl font-black tracking-tight">{selectedEmp.name} - Detailed Period Logs</h2>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{formatDateDDMMYYYY(reportFilters.startDate)} — {formatDateDDMMYYYY(reportFilters.endDate)}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedEmp(null)} className="p-2 hover:bg-white/10 rounded-xl transition-all opacity-60 hover:opacity-100"><FiXCircle size={28} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                            {reportLoading ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <div className="w-12 h-12 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Compiling Daily Logs...</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-widest sticky top-0 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-4 w-12 text-center">#</th>
                                                <th className="px-6 py-4">Attendance Date</th>
                                                <th className="px-6 py-4">Clocked-In</th>
                                                <th className="px-6 py-4">Clocked-Out</th>
                                                <th className="px-6 py-4 text-center">Deviation (Late)</th>
                                                <th className="px-6 py-4 text-center">Overtime</th>
                                                <th className="px-6 py-4 text-center">Production</th>
                                                <th className="px-6 py-4">Punches Found</th>
                                                <th className="px-6 py-4 text-center">Final Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {reportData.map((d, idx) => (
                                                <tr key={idx} className="hover:bg-[#C30010]/5 transition-colors">
                                                    <td className="px-6 py-4 text-center text-gray-400 font-mono text-xs">{idx + 1}</td>
                                                    <td className="px-6 py-4 font-black text-gray-700">{formatDateDDMMYYYY(d.attendance_date)}</td>
                                                    <td className="px-6 py-4 font-bold text-emerald-600">{d.check_in || '--:--'}</td>
                                                    <td className="px-6 py-4 font-bold text-rose-600">{d.check_out || '--:--'}</td>
                                                    <td className="px-6 py-4 text-center font-medium text-amber-600">{calculateLate(d.check_in)}</td>
                                                    <td className="px-6 py-4 text-center font-medium text-blue-600">{calculateOvertime(d.check_out)}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        {(() => {
                                                            const prod = calculateProduction(d.check_in, d.check_out);
                                                            return prod !== '-' ? (
                                                                <span className={`px-2 py-1 rounded-md text-[10px] font-black text-white shadow-sm flex items-center gap-1 w-fit mx-auto
                                                                    ${parseFloat(prod) >= 8 ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                                                    <FiClock size={10} /> {prod}
                                                                </span>
                                                            ) : <span className="text-gray-300">-</span>;
                                                        })()}
                                                    </td>
                                                    <td className="px-6 py-4 text-[10px] font-mono text-gray-400 max-w-[200px] truncate">{d.punch_records || 'No Logs'}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest
                                                            ${d.status === 'PRESENT' ? 'bg-emerald-100 text-emerald-700' :
                                                                d.status === 'ABSENT' ? 'bg-rose-100 text-rose-700' :
                                                                    d.status === 'LATE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                                                            {d.status || 'ABSENT'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* STATUS DATES POPUP */}
            {statusDatesPopup && (
                <div className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col border border-white/20 animate-scale-in">
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-r from-[#C30010] to-[#A00008] text-white flex justify-between items-center shadow-lg">
                            <div>
                                <h2 className="text-xl font-black tracking-tight">{statusDatesPopup.userName}</h2>
                                <p className="text-xs font-bold opacity-90 uppercase tracking-widest mt-1">
                                    {statusDatesPopup.status.replace('_', ' ')} Days - {statusDatesPopup.dates?.length || 0} Total
                                </p>
                            </div>
                            <button
                                onClick={() => setStatusDatesPopup(null)}
                                className="p-2 hover:bg-white/20 rounded-xl transition-all"
                            >
                                <FiXCircle size={28} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                            {statusDatesLoading ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <div className="w-12 h-12 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Loading Dates...</p>
                                </div>
                            ) : statusDatesPopup.dates?.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <FiAlertCircle size={48} className="text-gray-300" />
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No {statusDatesPopup.status} Days Found</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-100 text-gray-700 text-[10px] font-black uppercase tracking-widest sticky top-0 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-4 w-12 text-center">#</th>
                                                <th className="px-6 py-4">Date</th>
                                                <th className="px-6 py-4">Check-In</th>
                                                <th className="px-6 py-4">Check-Out</th>
                                                <th className="px-6 py-4">Punch Records</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {statusDatesPopup.dates.map((d, idx) => (
                                                <tr key={idx} className="hover:bg-[#C30010]/5 transition-colors">
                                                    <td className="px-6 py-4 text-center text-gray-400 font-mono text-xs">{idx + 1}</td>
                                                    <td className="px-6 py-4 font-black text-gray-700">{formatDateDDMMYYYY(d.date)}</td>
                                                    <td className="px-6 py-4 font-bold text-emerald-600">{d.check_in}</td>
                                                    <td className="px-6 py-4 font-bold text-rose-600">{d.check_out}</td>
                                                    <td className="px-6 py-4 text-xs text-gray-500 font-mono">{d.punch_records}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-gray-100 border-t border-gray-200 flex justify-between items-center">
                            <span className="text-xs text-gray-500 font-medium">
                                Period: {formatDateDDMMYYYY(reportFilters.startDate)} - {formatDateDDMMYYYY(reportFilters.endDate)}
                            </span>
                            <button
                                onClick={() => setStatusDatesPopup(null)}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl text-xs font-black uppercase tracking-tight transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            {/* Top Header & Re-calculate */}
            <div className="flex flex-col md:flex-row justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Attendance Desk</h2>
                    <p className="text-xs text-gray-500 mt-1">Manage employee attendance, reports, and logs.</p>
                </div>
                <div>
                    {/* Re-calculate button kept as requested */}
                    <button
                        onClick={() => handleRecalculate()}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition shadow-sm"
                    >
                        <FiRefreshCw /> Fetch & Update
                    </button>
                </div>
            </div>

            {/* Submenu / Tab Navigation */}
            <div className="flex items-center gap-8 border-b border-gray-200 px-2">
                <button
                    onClick={() => setCurrentView('dashboard')}
                    className={`pb-3 text-sm font-bold transition-all relative ${currentView === 'dashboard' ? 'text-gray-800 border-b-2 border-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Dashboard
                </button>
                <button
                    onClick={() => setCurrentView('register')}
                    className={`pb-3 text-sm font-bold transition-all relative ${currentView === 'register' ? 'text-gray-800 border-b-2 border-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Attendance Register
                </button>
                <button
                    onClick={() => setCurrentView('report')}
                    className={`pb-3 text-sm font-bold transition-all relative ${currentView === 'report' ? 'text-gray-800 border-b-2 border-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Attendance Report
                </button>
            </div>


            {/* View Switching */}
            {currentView === 'dashboard' && <DashboardView />}
            {currentView === 'report' && ReportViewContent}
            {currentView === 'register' && (

                <RegisterView
                    registerDate={registerDate}
                    setRegisterDate={setRegisterDate}
                    handleManualClick={handleManualClick}
                    refreshTrigger={refreshTrigger}
                    handlePunchEdit={handlePunchEdit}
                    isHROrAdmin={isHROrAdmin}
                />
            )}
            {/* Modal for Manual Entry / View Details */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">
                                {(modalMode === 'EDIT' || modalMode === 'EDIT_PUNCH')
                                    ? `Mark ${modalData?.newStatus ? modalData.newStatus.replace('ON_', '') : 'Status'}`
                                    : 'Status Details'
                                }
                            </h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><FiXCircle size={24} /></button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Common Info */}
                            <div className="flex justify-between items-center">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Employee</label>
                                    <p className="font-medium text-gray-800">{modalData?.name}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <p className="font-medium text-gray-800">{formatDateDDMMYYYY(modalData?.date)}</p>
                                </div>
                            </div>

                            {/* VIEW MODE CONTENT */}
                            {modalMode === 'VIEW' && (
                                <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Current Status</label>
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${modalData.color || 'bg-gray-200'}`}>{modalData.status}</span>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Source</label>
                                            <span className="text-xs font-bold text-blue-600">
                                                {modalData.is_manual_override == 1 ? 'Manual Override' : (modalData.sched_reason ? 'Scheduled (System)' : 'Auto-Calculated')}
                                            </span>
                                        </div>
                                    </div>

                                    {(modalData.permission_start_time || modalData.permission_end_time) && (
                                        <div className="flex justify-between items-center bg-purple-50 p-2 rounded border border-purple-100">
                                            <span className="text-xs font-bold text-purple-700">Permission Time</span>
                                            <span className="text-xs font-bold text-gray-700">
                                                {modalData.permission_start_time || '--:--'} to {modalData.permission_end_time || '--:--'}
                                            </span>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Notes</label>
                                        <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                                            {modalData.sched_reason ? (
                                                <span className="text-purple-700 font-medium">{modalData.sched_reason}</span>
                                            ) : modalData.override_reason ? (
                                                <span className="text-orange-700 font-medium">{modalData.override_reason}</span>
                                            ) : (
                                                <span className="text-gray-400 italic">No specific reason logged.</span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Edit Option */}
                                    <div className="pt-2">
                                        <button
                                            onClick={() => {
                                                setManualReason(modalData.override_reason || '');
                                                setModalData({ ...modalData, newStatus: modalData.status });
                                                setModalMode('EDIT');
                                            }}
                                            className="text-blue-600 text-xs font-bold hover:underline flex items-center gap-1"
                                        >
                                            <FiEdit2 /> Edit Status / Reason
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* EDIT MODE CONTENT */}
                            {modalMode === 'EDIT' && (
                                <div className="space-y-4">
                                    {(modalData?.newStatus === 'ON_LEAVE' || modalData?.newStatus === 'HALF_DAY') && (
                                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 mb-2">
                                            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Leave Type</label>
                                            <select
                                                className="w-full border-gray-300 p-2 rounded-lg text-sm shadow-sm"
                                                value={modalData.newStatus}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === 'HALF_DAY') {
                                                        setModalData(prev => ({ ...prev, newStatus: 'HALF_DAY' }));
                                                        setEditCheckIn('09:00');
                                                        setEditCheckOut('14:00');
                                                    } else {
                                                        setModalData(prev => ({ ...prev, newStatus: 'ON_LEAVE' }));
                                                        setEditCheckIn('');
                                                        setEditCheckOut('');
                                                    }
                                                }}
                                            >
                                                <option value="ON_LEAVE">Full Day</option>
                                                <option value="HALF_DAY">Half Day</option>
                                            </select>
                                            {modalData.newStatus === 'HALF_DAY' && (
                                                <div className="mt-2 text-[10px] text-orange-600 font-bold italic">
                                                    Timing: 09:00 AM to 02:00 PM
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {modalData?.newStatus === 'PERMISSION' && (
                                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 space-y-3">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Select Duration</label>
                                                <select
                                                    className="w-full border-gray-300 p-2 rounded-lg text-sm shadow-sm"
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '1hr') {
                                                            setModalData(prev => ({ ...prev, permission_start_time: '09:00', permission_end_time: '10:00' }));
                                                        } else if (val === '2hr') {
                                                            setModalData(prev => ({ ...prev, permission_start_time: '09:00', permission_end_time: '11:00' }));
                                                        }
                                                    }}
                                                >
                                                    <option value="">Custom...</option>
                                                    <option value="1hr">1 hr (09:00 - 10:00)</option>
                                                    <option value="2hr">2 hrs (09:00 - 11:00)</option>
                                                </select>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">From Time</label>
                                                    <input type="time" className="w-full border-gray-200 p-2 rounded-lg text-sm"
                                                        value={modalData.permission_start_time || ''}
                                                        onChange={e => setModalData({ ...modalData, permission_start_time: e.target.value })} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">To Time</label>
                                                    <input type="time" className="w-full border-gray-200 p-2 rounded-lg text-sm"
                                                        value={modalData.permission_end_time || ''}
                                                        onChange={e => setModalData({ ...modalData, permission_end_time: e.target.value })} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Note <span className="text-red-500">*</span></label>
                                        <textarea
                                            className="w-full border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                                            rows="3"
                                            placeholder="Enter reason..."
                                            value={manualReason}
                                            onChange={e => setManualReason(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {/* EDIT_PUNCH MODE CONTENT */}
                            {modalMode === 'EDIT_PUNCH' && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Check In Time</label>
                                            <input
                                                type="time"
                                                className="w-full border-gray-300 p-2 rounded-lg text-sm"
                                                value={editCheckIn}
                                                onChange={e => setEditCheckIn(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Check Out Time</label>
                                            <input
                                                type="time"
                                                className="w-full border-gray-300 p-2 rounded-lg text-sm"
                                                value={editCheckOut}
                                                onChange={e => setEditCheckOut(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Note <span className="text-red-500">*</span></label>
                                        <textarea
                                            className="w-full border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                                            rows="2"
                                            placeholder="Reason for manual punch update..."
                                            value={manualReason}
                                            onChange={e => setManualReason(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {/* Footer Buttons */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setModalOpen(false)}
                                    className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50"
                                >
                                    {modalMode === 'VIEW' ? 'Close' : 'Cancel'}
                                </button>
                                {(modalMode === 'EDIT' || modalMode === 'EDIT_PUNCH') && (
                                    <button
                                        onClick={submitManualUpdate}
                                        className={`flex-1 py-2 rounded-lg font-bold text-sm text-white shadow-md transition-all 
                                            ${(modalData?.newStatus === 'ON_LEAVE' || modalData?.newStatus === 'HALF_DAY') ? 'bg-orange-600 hover:bg-orange-700' :
                                                modalData?.newStatus === 'ON_DUTY' ? 'bg-blue-600 hover:bg-blue-700' :
                                                    modalData?.newStatus === 'PRESENT' ? 'bg-green-600 hover:bg-green-700' :
                                                        modalData?.newStatus === 'ABSENT' ? 'bg-red-600 hover:bg-red-700' :
                                                            'bg-purple-600 hover:bg-purple-700'}`}
                                    >
                                        Confirm
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceDashboard;

